import React, { useState, Suspense } from 'react';
import { UsersIcon, BookOpenIcon, CalendarIcon, SearchIcon, PlusIcon } from 'lucide-react';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import GenreTag from '../components/UI/GenreTag';
const BookClubs = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const bookClubs = [{
    id: 1,
    name: 'Mystery Mavens',
    genre: 'Mystery',
    members: 128,
    currentBook: 'The Silent Echo',
    nextMeeting: 'Oct 15, 2023 • 7:00 PM',
    format: 'Virtual',
    description: 'A club dedicated to mystery and thriller novels. We love plot twists and whodunits!',
    coverImage: 'https://images.unsplash.com/photo-1585858229735-a08286954395?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    tags: ['Thriller', 'Crime', 'Suspense']
  }, {
    id: 2,
    name: 'Romance Readers',
    genre: 'Romance',
    members: 156,
    currentBook: 'Beyond the Veil',
    nextMeeting: 'Oct 18, 2023 • 6:30 PM',
    format: 'In-Person',
    description: 'Exploring love stories across all genres, from contemporary to historical and fantasy romance.',
    coverImage: 'https://images.unsplash.com/photo-1518621736915-f3b1c41bfd00?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    tags: ['Contemporary', 'Historical', 'Fantasy']
  }, {
    id: 3,
    name: 'Fantasy Worlds',
    genre: 'Fantasy',
    members: 203,
    currentBook: 'The Crystal Kingdom',
    nextMeeting: 'Oct 20, 2023 • 8:00 PM',
    format: 'Virtual',
    description: 'Dive into magical realms and epic adventures. We discuss world-building, magic systems, and lore.',
    coverImage: 'https://images.unsplash.com/photo-1535905557558-afc4877a26fc?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    tags: ['High Fantasy', 'Magic', 'Dragons']
  }, {
    id: 4,
    name: 'Sci-Fi Society',
    genre: 'Sci-Fi',
    members: 89,
    currentBook: 'Quantum Dreams',
    nextMeeting: 'Oct 22, 2023 • 7:30 PM',
    format: 'Virtual',
    description: 'Exploring the future, space, and technology through speculative fiction.',
    coverImage: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    tags: ['Space Opera', 'Cyberpunk', 'Dystopian']
  }, {
    id: 5,
    name: 'Horror Hive',
    genre: 'Horror',
    members: 74,
    currentBook: 'Midnight Shadows',
    nextMeeting: 'Oct 25, 2023 • 9:00 PM',
    format: 'Virtual',
    description: 'For those who love to be scared. We read everything from gothic horror to slasher thrillers.',
    coverImage: 'https://images.unsplash.com/photo-1505635552518-3448ff116af3?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    tags: ['Gothic', 'Supernatural', 'Psychological']
  }, {
    id: 6,
    name: 'Literary Circle',
    genre: 'Literary',
    members: 112,
    currentBook: 'Whispers in the Wind',
    nextMeeting: 'Oct 28, 2023 • 2:00 PM',
    format: 'In-Person',
    description: 'Thoughtful discussions on contemporary and classic literary fiction.',
    coverImage: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
    tags: ['Classics', 'Contemporary', 'Poetry']
  }];
  const filteredClubs = bookClubs.filter(club => {
    const matchesSearch = club.name.toLowerCase().includes(searchQuery.toLowerCase()) || club.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = selectedGenre === 'All' || club.genre === selectedGenre;
    return matchesSearch && matchesGenre;
  });
  const genres = ['All', 'Mystery', 'Romance', 'Fantasy', 'Sci-Fi', 'Horror', 'Literary'];
  return <div className={`min-h-screen ${dk.page} py-8`} data-id="element-7593" data-mp="2q14cj95efg6h">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-7594" data-mp="2q0l5inhaec78">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4" data-id="element-7595" data-mp="33r7sxjhlkfo0">
          <div data-id="element-7596" data-mp="2zrz14k64arv3">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-7597" data-mp="2zrfs5syg3dt7">
              Book Clubs
            </h1>
            <p className={`mt-2 text-lg ${dk.textSecondary}`} data-id="element-7598" data-mp="2c1lgvftrxxb2">
              Join discussions with fellow readers
            </p>
          </div>
          <button className={`flex items-center px-4 py-2 rounded-lg text-white shadow-sm transition-colors ${rt.primaryButton}`} data-id="element-7599" data-mp="2l2jcb9q5cwi1">
            <PlusIcon className="h-5 w-5 mr-2" data-id="element-7600" data-mp="2g1ly1889cmlt" />
            Create a Club
          </button>
        </div>

        {/* Search & Filter */}
        <div className="mb-8 space-y-4" data-id="element-7601" data-mp="3ric3gp4uxaed">
          <div className="relative max-w-md" data-id="element-7602" data-mp="3rhka7kyqfor9">
            <SearchIcon className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${dk.textMuted}`} data-id="element-7603" data-mp="3tq66fy281oh" />
            <input type="text" placeholder="Search book clubs..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${dk.input}`} data-id="element-7604" data-mp="7w380t9orffz" />
          </div>

          <div className="flex flex-wrap gap-2" data-id="element-7605" data-mp="rjaw5ismrz79">
            {genres.map(genre => <button key={genre} onClick={() => setSelectedGenre(genre)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${selectedGenre === genre ? `${rt.bgDark} text-white` : `${dk.badgeNeutral} hover:opacity-80`}`} data-id="element-7606" data-mp="rl3yilw7nx9g">
                {genre}
              </button>)}
          </div>
        </div>

        {/* Club Grid */}
        {filteredClubs.length > 0 ? <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-id="element-7607" data-mp="1k591prhdjq3w">
            {filteredClubs.map(club => <div key={club.id} className={`rounded-xl shadow-sm overflow-hidden border flex flex-col h-full ${dk.card}`} data-id="element-7608" data-mp="1f4lbv94vtfb9">
                {/* Cover Image Strip */}
                <div className="h-32 w-full bg-cover bg-center relative" style={{
            backgroundImage: `url(${club.coverImage})`
          }} data-id="element-7609" data-mp="168c9pmpis2lf">
                  <div className="absolute inset-0 bg-black/30" data-id="element-7610" data-mp="rgtx7yvvcq6t"></div>
                  <div className="absolute bottom-3 left-3" data-id="element-7611" data-mp="reqzstsdzus2">
                    <GenreTag genre={club.genre} className="text-xs py-0.5 px-2 shadow-sm" data-id="element-7612" data-mp="mjbn456484x8" />
                  </div>
                </div>

                <div className="p-5 flex-1 flex flex-col" data-id="element-7613" data-mp="din8p9ct82jt">
                  <div className="flex justify-between items-start mb-2" data-id="element-7614" data-mp="2p4xr830tj62g">
                    <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-7615" data-mp="2p70ggku5cv64">
                      {club.name}
                    </h2>
                    <span className={`text-xs px-2 py-1 ${rt.badgeRadius} font-medium ${club.format === 'Virtual' ? dk.isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700' : dk.isDark ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-700'}`} data-id="element-7616" data-mp="2y4u8vkosj4o0">
                      {club.format}
                    </span>
                  </div>

                  <p className={`text-sm mb-4 line-clamp-2 ${dk.textSecondary}`} data-id="element-7617" data-mp="2femcdlfljcgs">
                    {club.description}
                  </p>

                  <div className="space-y-3 mt-auto" data-id="element-7618" data-mp="1mqgpas12lbds">
                    <div className={`flex items-center text-sm ${dk.textMuted}`} data-id="element-7619" data-mp="1mpx9bguzcx2e">
                      <UsersIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-7620" data-mp="l1y1wq3cgfrf" />
                      {club.members} members
                    </div>
                    <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-7621" data-mp="g3wig420aeff">
                      <BookOpenIcon className={`h-4 w-4 mr-2 flex-shrink-0 ${rt.textPrimary}`} data-id="element-7622" data-mp="u3yyrluo0zsk" />
                      <span className="truncate" data-id="element-7623" data-mp="140ac8f5zrvol">
                        Reading: {club.currentBook}
                      </span>
                    </div>
                    <div className={`flex items-center text-sm ${dk.textMuted}`} data-id="element-7624" data-mp="z1gz4numa6oy">
                      <CalendarIcon className="h-4 w-4 mr-2 flex-shrink-0" data-id="element-7625" data-mp="18rjyd5sc0gvq" />
                      {club.nextMeeting}
                    </div>
                  </div>

                  <button className={`mt-5 w-full py-2 px-4 rounded-lg text-white font-medium shadow-sm transition-colors ${rt.primaryButton}`} data-id="element-7626" data-mp="1mrl16qs5pydu">
                    Join Club
                  </button>
                </div>
              </div>)}
          </div> : <div className="text-center py-12" data-id="element-7627" data-mp="26aqxeb7oby8x">
            <UsersIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textFaint}`} data-id="element-7628" data-mp="1scahpuru2xen" />
            <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-7629" data-mp="1wee43eg1jmep">
              No book clubs found
            </h3>
            <p className={dk.textMuted} data-id="element-7630" data-mp="1imqw4hcqkpg3">Try adjusting your search or filters</p>
          </div>}
      </div>
    </div>;
};
export default BookClubs;